import {
  addDaysToIsoDay,
  endOfDayInstant,
  getActiveTimeZone,
  isIsoDay,
  startOfDayInstant,
  todayIso,
} from '@/shared/format';

/**
 * The console's own reading of "this month", "last month", "last 30 days" and "last 90 days"
 * (13 §3, AM-130 §1.2): a starting point for the range bar, in the tenant's calendar (the zone the
 * session carries, the one every date of the console is shown in) and never the browser's. The
 * server is the one that resolves `from`/`to` in the tenant's time zone and answers with the
 * window it actually used (`DashboardResponse.from`/`to`), so this is only ever a first guess, and
 * one that agrees with it from any browser.
 */

export const RANGE_PRESETS = ['thisMonth', 'lastMonth', 'last30', 'last90'] as const;

/** Every preset `rangeOfPreset` knows, whatever board asks for it (13 §4.11: reports add `thisYear`). */
export const ALL_RANGE_PRESETS = [
  'thisMonth',
  'lastMonth',
  'last30',
  'last90',
  'thisYear',
] as const;

export type RangePreset = (typeof ALL_RANGE_PRESETS)[number];

export interface DateRange {
  from: string;
  to: string;
}

/**
 * A search-string value that is a `yyyy-MM-dd` naming a day that exists, or `undefined` otherwise -
 * never thrown on. `2026-02-30` is not one: `Date` would read it as 2 March.
 */
export function parseIsoDate(value: unknown): string | undefined {
  return isIsoDay(value) ? value : undefined;
}

/**
 * The first and the last instant of the range's days in the tenant's zone, as the
 * `createdAt[from|to]` filters of the audit trails take them (tenant and platform alike). A `Z`
 * appended to the date made it the UTC day, and an action of 22:00 in Buenos Aires fell on the
 * next one; the browser's own midnight made it the viewer's day, and the same range read from
 * Madrid cut at another instant than from Buenos Aires. A range with an end that is not a real day
 * (the search string is parsed with {@link parseIsoDate}, but this is also handed a range by
 * code) is the default window instead of a `RangeError` that takes the screen down.
 */
export function localDayBounds(
  range: DateRange,
  timeZone: string = getActiveTimeZone(),
): { from: string; to: string } {
  if (!isIsoDay(range.from) || !isIsoDay(range.to)) {
    return localDayBounds(defaultRange(new Date(), timeZone), timeZone);
  }

  return {
    from: startOfDayInstant(range.from, timeZone).toISOString(),
    to: endOfDayInstant(range.to, timeZone).toISOString(),
  };
}

/** The first day of the month that is `monthsBack` months before the one `day` (`yyyy-MM-dd`) is in. */
function firstOfMonth(day: string, monthsBack: number): string {
  const year = Number(day.slice(0, 4));
  const month = Number(day.slice(5, 7));
  return new Date(Date.UTC(year, month - 1 - monthsBack, 1)).toISOString().slice(0, 10);
}

export function rangeOfPreset(
  preset: RangePreset,
  today: Date = new Date(),
  timeZone: string = getActiveTimeZone(),
): DateRange {
  // Plain calendar arithmetic over the day "today" is in the tenant's zone: no instant is moved by
  // a browser clock or a change of daylight-saving time.
  const day = todayIso(today, timeZone);
  switch (preset) {
    case 'lastMonth':
      return { from: firstOfMonth(day, 1), to: addDaysToIsoDay(firstOfMonth(day, 0), -1) };
    case 'last30':
      return { from: addDaysToIsoDay(day, -29), to: day };
    case 'last90':
      return { from: addDaysToIsoDay(day, -89), to: day };
    case 'thisYear':
      return { from: `${day.slice(0, 4)}-01-01`, to: day };
    case 'thisMonth':
    default:
      return { from: firstOfMonth(day, 0), to: day };
  }
}

/**
 * The reports' reading of the same presets (AM-301): "Este mes" and "Este año" are the whole month
 * and the whole year, not cut at today. A report is read by its period, and what the period bills
 * without a day of its own (the fee of a retainer) falls on its last day: cut at today, the
 * running month never showed its own fee. The others are the boards' own.
 */
export function reportRangeOfPreset(
  preset: RangePreset,
  today: Date = new Date(),
  timeZone: string = getActiveTimeZone(),
): DateRange {
  const day = todayIso(today, timeZone);
  switch (preset) {
    case 'thisMonth':
      return { from: firstOfMonth(day, 0), to: addDaysToIsoDay(firstOfMonth(day, -1), -1) };
    case 'thisYear':
      return { from: `${day.slice(0, 4)}-01-01`, to: `${day.slice(0, 4)}-12-31` };
    default:
      return rangeOfPreset(preset, today, timeZone);
  }
}

/** The window a report opens with when the URL names none: the whole running month (AM-301). */
export function defaultReportRange(
  today: Date = new Date(),
  timeZone: string = getActiveTimeZone(),
): DateRange {
  return reportRangeOfPreset('thisMonth', today, timeZone);
}

/** The default window a board opens with when the URL names none: the running month (13 §1.2). */
export function defaultRange(
  today: Date = new Date(),
  timeZone: string = getActiveTimeZone(),
): DateRange {
  return rangeOfPreset('thisMonth', today, timeZone);
}

export interface DashboardSearch {
  from?: string;
  to?: string;
}

/** `validateSearch` of `/` (13 §3): a malformed `from`/`to` is dropped, never thrown on. */
export function dashboardSearchSchema(search: Record<string, unknown>): DashboardSearch {
  const from = parseIsoDate(search['from']);
  const to = parseIsoDate(search['to']);
  return from === undefined || to === undefined ? {} : { from, to };
}
